import { Box, Tab as MuiTab, Tabs, TabsProps, TextField } from '@mui/material'
import React, { ChangeEvent } from 'react'
import { Tab } from '../../../types'
import { useBlockBrowserAutofill } from '../../../hooks/use-block-browser-autofill'

type SearchFieldProps = {
  searchValue?: string
  onSearchChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  placeholder?: string
}

const SearchField = ({
  searchValue,
  onSearchChange,
  placeholder = 'Search',
}: SearchFieldProps) => {
  const { blockBrowserAutofill, domFieldName, domFieldId, htmlInputProps } =
    useBlockBrowserAutofill('search', true)

  return (
    <TextField
      type="text"
      placeholder={placeholder}
      variant="outlined"
      size="small"
      name={domFieldName}
      id={domFieldId}
      value={searchValue ?? ''}
      onChange={onSearchChange}
      sx={{ width: '100%' }}
      slotProps={{
        htmlInput: {
          ...htmlInputProps,
          autoComplete: blockBrowserAutofill ? 'new-password' : 'off',
        },
      }}
    />
  )
}

type BasicTabsWithSearchProps = {
  tabs: Tab[]
  value: number
  handleChange: (_event: React.SyntheticEvent, newValue: number) => void
  children: React.ReactNode

  searchValue?: string
  searchPlaceholder?: string

  onSearchChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
} & Omit<TabsProps, 'children' | 'value' | 'onChange'>

const BasicTabsWithSearch = ({
  tabs,
  value,
  handleChange,
  children,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  sx,
}: BasicTabsWithSearchProps) => {
  return (
    <Box
      sx={[
        {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      <Box
        sx={{
          flexShrink: 0,
          zIndex: 9,
          bgcolor: 'background.paper',
          pt: 1.5,
          pb: 0.75,
        }}
      >
        {onSearchChange ? (
          <SearchField
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
        ) : (
          <SearchField placeholder={searchPlaceholder} />
        )}
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          sx={{
            bgcolor: 'background.paper',
            '& .MuiTabs-scrollButtons': {
              display: 'none',
            },
            '& .Mui-selected': {
              fontWeight: 700,
            },
            '& .MuiTab-root.Mui-disabled': {
              opacity: 0.55,
              color: 'text.primary',
              cursor: 'not-allowed',
            },
          }}
        >
          {tabs.map((tab) => (
            <MuiTab
              key={tab.id}
              label={tab.label}
              disabled={tab.disabled}
              sx={{
                textTransform: 'capitalize',
                display: 'flex',
                justifyContent: 'space-between',
                visibility: 'visible',
                '&.Mui-disabled': {
                  opacity: 0.55,
                  color: 'text.primary',
                  cursor: 'not-allowed',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          overscrollBehaviorY: 'contain',
          overscrollBehaviorX: 'none',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default BasicTabsWithSearch
